import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Edit3, Download, Share2, CheckSquare, X, AlertTriangle, Coffee, Upload, Lock, Unlock, Bell, Megaphone, EyeOff, Send, RefreshCcw, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmModal";
import ImportExcelModal from "@/components/ImportExcelModal";
import RequestAvailabilityModal from "@/components/RequestAvailabilityModal";
import EventStepper from "@/components/EventStepper";
import StepperOnboarding, { StepperOnboardingButton } from "@/components/StepperOnboarding";
import HelpHint from "@/components/HelpHint";
import DatePublishMenu from "@/components/DatePublishMenu";
import ClearScheduleModal from "@/components/ClearScheduleModal";
import ResetRefereesModal from "@/components/ResetRefereesModal";
import SortHeader, { useSort } from "@/components/SortHeader";
import SlotsEditor from "@/components/SlotsEditor";
import NotifyCompleteModal from "@/components/NotifyCompleteModal";
import PackagePicker from "@/components/PackagePicker";
import PublishHistoryModal from "@/components/PublishHistoryModal";
import StaffShareModal from "@/components/StaffShareModal";
import SnapshotsModal from "@/components/SnapshotsModal";
import AvailabilitiesTab from "@/components/event/AvailabilitiesTab";
import BreakModal from "@/components/event/BreakModal";
import ShareModal from "@/components/event/ShareModal";
import SummaryView from "@/components/event/SummaryView";
import AutoAssignModal from "@/components/event/AutoAssignModal";
import BulkScorerModal from "@/components/event/BulkScorerModal";
import BulkSwapRefModal from "@/components/event/BulkSwapRefModal";
import { DEFAULT_SLOTS, SLOT_KEYS, SLOT_LABELS, resolveSlots, resolveSlotsForDate, slotForTime, getSlotAnswer } from "@/lib/slots";
import { addMinutes, computeDuration, compareSurface, detectPackage, autoDetectedPackage, formatRelative, formatSurface, roleLabel, sortGames, uid } from "@/lib/utils";
import { isPackageIncomplete } from "@/lib/packages";
import { findConflicts, summarizeConflicts } from "@/lib/conflicts";
import { generateClientPDF } from "@/lib/pdf";
import OfficialPayrollRow from "@/components/OfficialPayrollRow";

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [event, setEvent] = useState(null);
  const [officials, setOfficials] = useState([]);
  const [grades, setGrades] = useState([]);
  const [settings, setSettings] = useState({ packages: {} });
  const [tab, setTab] = useState("schedule");
  const [editing, setEditing] = useState(null);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [availIndex, setAvailIndex] = useState({});
  const [confirmIndex, setConfirmIndex] = useState({});
  const [requestsCount, setRequestsCount] = useState(0);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showPublishHistory, setShowPublishHistory] = useState(false);
  const [showStaffShare, setShowStaffShare] = useState(false);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [showAutoAssign, setShowAutoAssign] = useState(false);
  const [showBulkScorer, setShowBulkScorer] = useState(false);
  const [showBulkSwap, setShowBulkSwap] = useState({ open: false, fromId: "", date: "" });

  // Crée un snapshot (best-effort) avant une opération destructive.
  // N'interrompt jamais le flux si la sauvegarde échoue.
  const takeSnapshot = async (reason) => {
    try {
      await api.post(`/events/${eventId}/snapshots`, { reason });
    } catch {
      // intentionnel — on continue malgré l'échec
    }
  };
  const [viewStep, setViewStep] = useState(null); // override pour cliquer dans le stepper
  const stepActionsRef = React.useRef(null);
  const [extras, setExtras] = useState([]);

  const refreshExtras = async (id) => {
    try {
      const { data } = await api.get(`/events/${id || eventId}/extras`);
      setExtras(data.extras || []);
    } catch {}
  };

  const refreshAvailabilities = async (id) => {
    try {
      const { data: res } = await api.get(`/events/${id || eventId}/availabilities`);
      // availIndex[officialId][date] = raw answer value (string OR { am, pm, soiree })
      const idx = {};
      (res.answers || []).forEach((a) => {
        if (!idx[a.official_id]) idx[a.official_id] = {};
        Object.entries(a.answers || {}).forEach(([d, v]) => {
          idx[a.official_id][d] = v;
        });
      });
      setAvailIndex(idx);
      const cidx = {};
      (res.confirmations || []).forEach((c) => {
        if (!cidx[c.official_id]) cidx[c.official_id] = new Set();
        cidx[c.official_id].add(c.game_id);
      });
      setConfirmIndex(cidx);
      setRequestsCount((res.requests || []).length);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const [ev, off, gr, st] = await Promise.all([
          api.get(`/events/${eventId}`),
          api.get(`/officials`),
          api.get(`/grades`),
          api.get(`/settings`),
        ]);
        setEvent(ev.data.event);
        setOfficials(off.data.officials);
        setGrades(gr.data.grades);
        setSettings(st.data.settings);
        refreshAvailabilities(eventId);
        refreshExtras(eventId);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const saveGames = async (newGames) => {
    try {
      const { data } = await api.put(`/events/${eventId}/games`, {
        games: newGames,
        // Verrou optimiste (Feb 17, 2026) : envoie la version courante que
        // ce client a vu. Le backend renvoie 409 si un co-admin a sauvegardé
        // entre-temps ; on rafraîchit alors les données et on prévient le user.
        expected_version: event?.version ?? 0,
      });
      setEvent(data.event);
      return data.event;
    } catch (e) {
      const status = e.response?.status;
      if (status === 409) {
        // Rafraîchit la vue avec les données actuelles pour que l'user
        // voie ce qui a changé, puis puisse ré-appliquer ses modifs.
        try {
          const { data: fresh } = await api.get(`/events/${eventId}`);
          setEvent(fresh.event);
        } catch (_) { /* noop */ }
        toast.error("Un autre utilisateur vient de modifier cet événement. Tes changements n'ont pas été sauvegardés — vérifie la vue rafraîchie et réapplique-les si besoin.");
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
      throw e; // propagate so callers (e.g. Import Excel) don't show a false "Réussi"
    }
  };

  const updateGame = (gid, patch) => {
    saveGames((event.games || []).map((g) => (g.id === gid ? { ...g, ...patch } : g)));
  };

  const removeGame = async (g) => {
    const ok = await confirm.ask({
      title: g.is_break ? "Supprimer cette pause?" : "Supprimer cette partie?",
      message: g.is_break
        ? `Pause "${g.break_label}" de ${g.start_time} à ${g.end_time}.`
        : `${g.team_a || "Équipe A"} vs ${g.team_b || "Équipe B"} à ${g.start_time}.`,
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    saveGames((event.games || []).filter((x) => x.id !== g.id));
  };

  const [showClearModal, setShowClearModal] = useState(false);
  const [showResetRefsModal, setShowResetRefsModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedGameIds, setSelectedGameIds] = useState(() => new Set());
  const openClearModal = () => setShowClearModal(true);
  const openResetRefsModal = () => setShowResetRefsModal(true);
  const handleClearConfirm = async (gamesToKeep) => {
    const removed = (event.games || []).length - gamesToKeep.length;
    try {
      await takeSnapshot("Avant effacement de l'horaire");
      await saveGames(gamesToKeep);
      toast.success(`${removed} entrée${removed > 1 ? "s" : ""} effacée${removed > 1 ? "s" : ""}`);
    } catch {
      // saveGames already showed the API error
    } finally {
      setShowClearModal(false);
    }
  };
  const handleResetRefsConfirm = async (newGames) => {
    const old = event.games || [];
    const removed = old.reduce((n, g) => {
      const same = newGames.find((x) => x.id === g.id);
      const before = (g.referee1_id ? 1 : 0) + (g.referee2_id ? 1 : 0);
      const after = same ? ((same.referee1_id ? 1 : 0) + (same.referee2_id ? 1 : 0)) : before;
      return n + (before - after);
    }, 0);
    try {
      await takeSnapshot("Avant réinitialisation des arbitres");
      await saveGames(newGames);
      toast.success(`${removed} assignation${removed > 1 ? "s" : ""} d'arbitre retirée${removed > 1 ? "s" : ""}`);
    } catch {
      // saveGames already showed the API error
    } finally {
      setShowResetRefsModal(false);
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode((v) => {
      if (v) setSelectedGameIds(new Set());
      return !v;
    });
  };
  const toggleSelectGame = (gameId) => {
    setSelectedGameIds((prev) => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId);
      else next.add(gameId);
      return next;
    });
  };
  const selectAllGames = (ids) => setSelectedGameIds(new Set(ids));
  const clearSelection = () => setSelectedGameIds(new Set());
  const deleteSelectedGames = async () => {
    if (selectedGameIds.size === 0) return;
    const ok = await confirm.ask({
      title: `Effacer ${selectedGameIds.size} partie${selectedGameIds.size > 1 ? "s" : ""} sélectionnée${selectedGameIds.size > 1 ? "s" : ""}?`,
      message: "Les assignations sur ces parties seront perdues. Action irréversible.",
      confirmLabel: "Effacer",
      danger: true,
    });
    if (!ok) return;
    const kept = (event.games || []).filter((g) => !selectedGameIds.has(g.id));
    const removed = (event.games || []).length - kept.length;
    try {
      await saveGames(kept);
      setSelectedGameIds(new Set());
      setSelectionMode(false);
      toast.success(`${removed} partie${removed > 1 ? "s" : ""} effacée${removed > 1 ? "s" : ""}`);
    } catch {
      // saveGames already showed the API error
    }
  };

  const applyPackageToSelected = async (pkg) => {
    if (selectedGameIds.size === 0) return;
    // pkg === null → réinitialise (Auto)
    const next = (event.games || []).map((g) =>
      selectedGameIds.has(g.id) && !g.is_break
        ? { ...g, package_override: pkg }
        : g
    );
    try {
      await saveGames(next);
      const label = pkg ? `Forfait ${pkg}` : "Auto";
      toast.success(`${label} appliqué à ${selectedGameIds.size} partie${selectedGameIds.size > 1 ? "s" : ""}`);
    } catch {
      // saveGames already showed the API error
    }
  };

  const addGame = () => {
    const newG = {
      id: uid(),
      surface: "1",
      date: event.date || "",
      start_time: "12:00",
      end_time: addMinutes("12:00", event.game_duration || 45),
      duration: event.game_duration || 45,
      team_a: "",
      team_b: "",
      category: "",
      referee1_id: null,
      referee2_id: null,
      scorer_id: null,
      is_break: false,
      break_label: "",
    };
    saveGames([...(event.games || []), newG]);
  };

  const addBreak = (label, durationMin, surface) => {
    const dur = parseInt(durationMin) || 30;
    const lastEnd = (event.games || []).reduce(
      (max, g) => (g.end_time > max ? g.end_time : max),
      event.start_time || "12:00"
    );
    const newBreaks = [];
    const surfaces = event.surfaces || 2;
    const targets = surface === "all" ? Array.from({ length: surfaces }, (_, i) => String(i + 1)) : [String(surface)];
    targets.forEach((s) => {
      newBreaks.push({
        id: uid(),
        surface: s,
        date: event.date || "",
        start_time: lastEnd,
        end_time: addMinutes(lastEnd, dur),
        duration: dur,
        is_break: true,
        break_label: label || "Pause",
        team_a: "",
        team_b: "",
        category: "",
        referee1_id: null,
        referee2_id: null,
        scorer_id: null,
      });
    });
    saveGames([...(event.games || []), ...newBreaks]);
  };

  const cascadeFromGame = (gameId, patch) => {
    const targetOld = event.games.find((g) => g.id === gameId);
    if (!targetOld) return;
    const targetSurface = patch.surface ?? targetOld.surface;
    const breakMin = event.break_between || 0;
    const sameSurface = event.games
      .filter((g) => String(g.surface) === String(targetSurface) && g.id !== gameId)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
    const affected = sameSurface.filter((g) => g.start_time >= targetOld.start_time);
    const newTarget = { ...targetOld, ...patch };
    let cursor = addMinutes(newTarget.end_time, breakMin);
    const shifted = {};
    affected.forEach((g) => {
      const dur = g.duration || computeDuration(g.start_time, g.end_time);
      const newStart = cursor;
      const newEnd = addMinutes(newStart, dur);
      shifted[g.id] = { start_time: newStart, end_time: newEnd };
      cursor = addMinutes(newEnd, breakMin);
    });
    const updated = event.games.map((g) => {
      if (g.id === gameId) return newTarget;
      if (shifted[g.id]) return { ...g, ...shifted[g.id] };
      return g;
    });
    saveGames(updated);
  };

  const closeEvent = async () => {
    const ok = await confirm.ask({
      title: "Fermer l'événement?",
      message: "L'horaire sera officiel et les paies pourront être générées. Tu pourras toujours rouvrir plus tard.",
      confirmLabel: "Fermer",
    });
    if (!ok) return;
    try {
      await api.post(`/events/${eventId}/close`);
      setEvent({ ...event, closed: true });
      toast.success("Événement fermé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reopenEvent = async () => {
    try {
      await api.post(`/events/${eventId}/reopen`);
      setEvent({ ...event, closed: false });
      toast.success("Événement rouvert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const publishEvent = async () => {
    try {
      const { data } = await api.post(`/events/${eventId}/publish`);
      setEvent({ ...event, published_at: data.published_at });
      toast.success("Horaire publié");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleAvailabilityLock = async () => {
    const next = !event.availability_locked;
    if (next) {
      const ok = await confirm.ask({
        title: "Verrouiller les disponibilités?",
        message: "Les officiels ne pourront plus modifier leurs réponses pour cet événement tant que tu ne déverrouilleras pas.",
        confirmLabel: "Verrouiller",
        danger: false,
      });
      if (!ok) return;
    }
    try {
      await api.put(`/events/${eventId}/availability-lock`, { locked: next });
      setEvent({ ...event, availability_locked: next });
      toast.success(next ? "Disponibilités verrouillées" : "Disponibilités déverrouillées");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remindUnconfirmed = async () => {
    const ok = await confirm.ask({
      title: "Envoyer un rappel SMS?",
      message: "Tous les officiels qui ont au moins une partie non confirmée recevront un SMS leur rappelant de se connecter et de confirmer leurs assignations.",
      confirmLabel: "Envoyer",
      danger: false,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/events/${eventId}/remind-unconfirmed`);
      if (data.reminded === 0 && data.no_phone === 0) {
        toast.success("Toutes les assignations sont confirmées 🎉");
      } else {
        const parts = [`${data.reminded} SMS envoyé${data.reminded > 1 ? "s" : ""}`];
        if (data.no_phone > 0) parts.push(`${data.no_phone} sans numéro`);
        if (data.failed > 0) parts.push(`${data.failed} échec${data.failed > 1 ? "s" : ""}`);
        toast.success(parts.join(" · "));
      }
      refreshAvailabilities(eventId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const publishAssignments = async (targetDate = null) => {
    const isFirst = !event.assignments_published_at;
    const evName = event.name || "cet événement";
    const scopeLabel = targetDate ? `du ${targetDate}` : "complet";
    const ok = await confirm.ask({
      title: isFirst
        ? `Diffuser les assignations · ${evName}${targetDate ? ` (${targetDate})` : ""}`
        : `Re-diffuser les assignations · ${evName}${targetDate ? ` (${targetDate})` : ""}`,
      message: isFirst
        ? `Cette action s'applique UNIQUEMENT à l'événement « ${evName} »${targetDate ? ` (journée ${targetDate})` : " (toutes les dates)"}. Les officiels assignés recevront un SMS leur demandant de confirmer.`
        : `Re-diffusion pour « ${evName} »${targetDate ? ` (${targetDate})` : " (événement complet)"}. Par défaut, seuls les officiels nouvellement assignés (pas encore notifiés) recevront un SMS.`,
      confirmLabel: isFirst ? "Diffuser" : "Re-diffuser",
      danger: false,
      options: isFirst ? null : [
        {
          key: "forceAll",
          label: "Renvoyer à TOUS les officiels assignés",
          help: "Même ceux qui ont déjà reçu un SMS pour cet événement.",
          default: false,
        },
      ],
    });
    if (!ok) return;
    const forceAll = typeof ok === "object" && ok.forceAll === true;
    try {
      const body = { ...(targetDate ? { date: targetDate } : {}), ...(forceAll ? { force_all: true } : {}) };
      const { data } = await api.post(`/events/${eventId}/publish-assignments`, body);
      setEvent({
        ...event,
        assignments_published_at: data.assignments_published_at,
        assignments_last_published_at: data.assignments_last_published_at,
        assignments_published_dates: data.assignments_published_dates || [],
      });
      const parts = [];
      if (data.sms_sent > 0) parts.push(`${data.sms_sent} SMS envoyé${data.sms_sent > 1 ? "s" : ""}`);
      if (data.no_phone > 0) parts.push(`${data.no_phone} sans numéro`);
      if (data.failed > 0) parts.push(`${data.failed} échec${data.failed > 1 ? "s" : ""}`);
      const evLabel = `« ${evName} »${targetDate ? ` (${targetDate})` : ""}${forceAll ? " · renvoi forcé" : ""}`;
      if (data.sms_sent === 0 && data.newly_notified === 0 && !isFirst) {
        toast.success(`Aucun nouvel officiel à notifier pour ${evLabel}`);
      } else {
        toast.success(parts.length ? `${parts.join(" · ")} pour ${evLabel}` : `Assignations diffusées pour ${evLabel}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const unpublishAssignments = async (targetDate = null) => {
    const scope = targetDate ? `du ${targetDate}` : "complètement";
    const ok = await confirm.ask({
      title: targetDate ? `Masquer les assignations ${scope}?` : "Cacher les assignations aux officiels?",
      message: targetDate
        ? `Les parties du ${targetDate} ne seront plus visibles aux officiels. Les autres dates restent inchangées.`
        : "Les officiels ne verront plus leurs parties tant que tu ne re-diffuses pas. Aucun SMS ne sera envoyé.",
      confirmLabel: "Masquer",
      danger: true,
    });
    if (!ok) return;
    try {
      const body = targetDate ? { date: targetDate } : {};
      const { data } = await api.post(`/events/${eventId}/unpublish-assignments`, body);
      setEvent({
        ...event,
        assignments_published_at: targetDate ? event.assignments_published_at : null,
        assignments_last_published_at: targetDate ? event.assignments_last_published_at : null,
        assignments_published_dates: data.assignments_published_dates ?? [],
      });
      toast.success(targetDate ? `Assignations du ${targetDate} masquées` : "Assignations masquées");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!event) {
    return (
      <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse" data-testid="event-loading">
        Chargement…
      </p>
    );
  }

  // Compteurs pour le stepper (recalculés à chaque render — peu coûteux)
  const playableGames = (event.games || []).filter((g) => !g.is_break);
  const totalAssignments = playableGames.reduce(
    (n, g) => n + (g.referee1_id ? 1 : 0) + (g.referee2_id ? 1 : 0) + (g.scorer_id ? 1 : 0),
    0
  );
  const confirmedCount = playableGames.reduce((n, g) => {
    let c = 0;
    [g.referee1_id, g.referee2_id, g.scorer_id].forEach((oid) => {
      if (oid && (confirmIndex || {})[oid]?.has(g.id)) c++;
    });
    return n + c;
  }, 0);

  // Effective time slots for this event (event override > settings > defaults)
  const effectiveSlots = resolveSlots(event, settings || {});
  // Helper passed to children: per-date slots resolution (uses availability_slots_per_date)
  const slotsForDate = (date) => resolveSlotsForDate(event, settings || {}, date);

  return (
    <div data-testid="event-detail-page">
      {confirm.modal}
      <StepperOnboarding />
      {showRequestModal && (
        <RequestAvailabilityModal
          officials={officials}
          initialEventId={event.id}
          onClose={() => setShowRequestModal(false)}
          onSent={() => {
            setShowRequestModal(false);
            refreshAvailabilities(event.id);
            toast.success("Demande envoyée — les officiels peuvent maintenant répondre");
          }}
        />
      )}
      {showClearModal && (
        <ClearScheduleModal
          games={event.games || []}
          onClose={() => setShowClearModal(false)}
          onConfirm={handleClearConfirm}
        />
      )}
      {showResetRefsModal && (
        <ResetRefereesModal
          games={event.games || []}
          onClose={() => setShowResetRefsModal(false)}
          onConfirm={handleResetRefsConfirm}
        />
      )}
      {shareOpen && <ShareModal event={event} onClose={() => setShareOpen(false)} />}
      {showPublishHistory && (
        <PublishHistoryModal
          eventId={event.id}
          eventName={event.name || ""}
          onClose={() => setShowPublishHistory(false)}
        />
      )}
      {showStaffShare && (
        <StaffShareModal
          eventId={event.id}
          eventName={event.name || ""}
          onClose={() => setShowStaffShare(false)}
        />
      )}
      {importOpen && (
        <ImportExcelModal
          event={event}
          onApply={async (newGames, importMode) => {
            try {
              const reasonByMode = {
                replace: "Avant import — Remplacer",
                append: "Avant import — Ajouter",
                sync: "Avant import — Synchroniser",
              };
              await takeSnapshot(reasonByMode[importMode] || "Avant import");
              const updated = await saveGames(newGames);
              const count = (updated?.games || []).filter((g) => !g.is_break).length;
              if (count === 0 && newGames.length === 0) {
                toast.error("Aucune partie détectée dans le fichier. Vérifie le mapping des colonnes.");
              } else {
                toast.success(`Importation réussie · ${count} partie(s)`);
              }
            } catch {
              // saveGames already showed an error toast
            }
          }}
          onClose={() => setImportOpen(false)}
        />
      )}
      {showSnapshots && (
        <SnapshotsModal
          eventId={event.id}
          eventName={event.name || ""}
          onClose={() => setShowSnapshots(false)}
          onRestored={async () => {
            try {
              const { data } = await api.get(`/events/${eventId}`);
              setEvent(data.event);
            } catch {
              // toast handled in interceptor
            }
          }}
        />
      )}
      {showAutoAssign && (
        <AutoAssignModal
          eventId={event.id}
          eventName={event.name || ""}
          officials={officials}
          hiddenOfficialIds={[
            ...((event.hidden_official_ids_by_role || {}).referee || []),
            ...((event.hidden_official_ids_by_role || {}).scorer || []),
            ...(event.hidden_official_ids || []),
          ]}
          games={event.games || []}
          surfaces={event.surfaces || 0}
          initialBlockedCategories={event.blocked_categories_by_official || {}}
          initialForbiddenPairs={event.forbidden_pairs || []}
          initialExcludedIds={event.excluded_official_ids || []}
          initialIsolatedSurfaces={event.isolated_surfaces || []}
          initialRestrictedSurfaces={event.restricted_surfaces_by_official || {}}
          onClose={() => setShowAutoAssign(false)}
          onApplied={async () => {
            try {
              const { data } = await api.get(`/events/${eventId}`);
              setEvent(data.event);
            } catch {
              // toast handled in interceptor
            }
          }}
        />
      )}
      {showBulkScorer && (
        <BulkScorerModal
          event={event}
          officials={officials}
          onClose={() => setShowBulkScorer(false)}
          onApplied={async () => {
            const { data } = await api.get(`/events/${eventId}`);
            setEvent(data.event);
          }}
        />
      )}
      {showBulkSwap.open && (
        <BulkSwapRefModal
          event={event}
          officials={officials}
          initialFromId={showBulkSwap.fromId}
          initialDate={showBulkSwap.date}
          onClose={() => setShowBulkSwap({ open: false, fromId: "", date: "" })}
          onApplied={async () => {
            const { data } = await api.get(`/events/${eventId}`);
            setEvent(data.event);
          }}
        />
      )}
      {showBreakModal && (
        <BreakModal
          event={event}
          onSave={(l, d, s) => {
            addBreak(l, d, s);
            setShowBreakModal(false);
          }}
          onClose={() => setShowBreakModal(false)}
        />
      )}

      <button onClick={() => navigate("/")} className="btn-ghost mb-6" data-testid="back-to-events-btn">
        <ArrowLeft size={14} strokeWidth={2.5} /> Retour aux événements
      </button>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div>
          <div className="label">
            {event.date}
            {event.location ? " · " + event.location : ""}
          </div>
          <h1 className="h1-big" data-testid="event-title">
            {event.name}
          </h1>
          {event.client && (
            <p className="font-mono text-xs text-[var(--muted)] mt-2">
              Client : <span className="text-[var(--text)]">{event.client}</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="inline-flex items-center">
            <button onClick={() => setShareOpen(true)} className="btn-secondary" data-testid="share-btn">
              <Share2 size={14} strokeWidth={2.5} /> Partager
            </button>
            <HelpHint
              title="Partager"
              text="Ouvre un menu pour copier le lien public de l'horaire client, ou pour envoyer un courriel/SMS de partage. Idéal pour transmettre l'horaire au tournoi ou au client."
              testid="hint-share"
            />
          </div>
          <div className="inline-flex items-center">
            <button
              onClick={() => setShowStaffShare(true)}
              className="btn-secondary"
              data-testid="staff-share-btn"
            >
              <Share2 size={14} strokeWidth={2.5} /> Partager au staff
            </button>
            <HelpHint
              title="Partager au staff (privé, lecture seule)"
              text="Génère un lien privé pour une personne qui t'aide à gérer le staff. Elle verra l'horaire complet et les officiels assignés, mais ne pourra rien modifier ni voir les paies, forfaits, coordonnées ou autres événements."
              testid="hint-staff-share"
            />
          </div>
          <div className="inline-flex items-center">
            <button
              onClick={() => setShowSnapshots(true)}
              className="btn-secondary"
              data-testid="open-snapshots-btn"
            >
              <Save size={14} strokeWidth={2.5} /> Sauvegardes
            </button>
            <HelpHint
              title="Sauvegardes de l'horaire"
              text="Crée automatiquement une copie de l'horaire avant chaque import, effacement ou réinitialisation. Restaure en 1 clic si une opération s'est mal passée. Max 10 sauvegardes par événement."
              testid="hint-snapshots"
            />
          </div>
          <div className="inline-flex items-center">
            <button
              onClick={() => setShowAutoAssign(true)}
              className="btn-secondary"
              data-testid="open-auto-assign-btn"
            >
              <Sparkles size={14} strokeWidth={2.5} /> Auto-assigner
            </button>
            <HelpHint
              title="Auto-assigner les arbitres"
              text="Remplit automatiquement referee1 + referee2 de chaque partie selon les catégories autorisées, disponibilités, max consécutif/jour et fairness. Aperçu avant application, snapshot auto."
              testid="hint-auto-assign"
            />
          </div>
          <div className="inline-flex items-center">
            <button
              onClick={() => setShowBulkScorer(true)}
              className="btn-secondary"
              data-testid="open-bulk-scorer-btn"
            >
              Marqueur en bloc
            </button>
            <HelpHint
              title="Marqueur en bloc"
              text="Assigne (ou détache) un marqueur à toutes les parties d'une surface pour une journée. Un dialogue confirme l'écrasement si un marqueur différent est déjà en place."
              testid="hint-bulk-scorer"
            />
          </div>
          <div className="inline-flex items-center">
            <button
              onClick={() => setShowBulkSwap({ open: true, fromId: "", date: "" })}
              className="btn-secondary"
              data-testid="open-bulk-swap-btn"
            >
              Remplacer arbitre
            </button>
            <HelpHint
              title="Remplacer un arbitre pour une journée"
              text="Réattribue en un clic toutes les parties d'un arbitre absent à un remplaçant pour la journée choisie. Détecte les conflits (dispos, catégorie, collision, duo interdit) — l'admin peut forcer."
              testid="hint-bulk-swap"
            />
          </div>
          <div className="inline-flex items-center">
            <button
              onClick={() => generateClientPDF(event, officials, grades, settings, extras)}
              className="btn-secondary"
              data-testid="generate-invoice-btn"
            >
              <Download size={14} strokeWidth={2.5} /> Facture (PDF)
            </button>
            <HelpHint
              title="Facture (PDF)"
              text="Génère un PDF imprimable avec la facture client (basée sur les tarifs Package A/B/C/D) et les feuilles de paie des officiels. Utilise window.print donc tu peux choisir Imprimer ou Sauvegarder en PDF."
              testid="hint-invoice"
            />
          </div>
        </div>
      </div>

      {event.published_at && (
        <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mb-4">
          Publié {formatRelative(event.published_at)}
        </p>
      )}

      <EventWorkflowStepper
        event={event}
        requestsCount={requestsCount}
        confirmedCount={confirmedCount}
        totalAssignments={totalAssignments}
        onStepClick={(s, stepNum, currentActive) => {
          // 1. Affiche les actions de l'étape cliquée
          setViewStep(stepNum);
          // 2. Bascule sur l'onglet pertinent
          if (s.id === "availability") setTab("availabilities");
          else if (s.id === "build" || s.id === "assign" || s.id === "confirm" || s.id === "publish" || s.id === "close") setTab("schedule");
          else if (s.id === "payroll") setTab("summary");
          // 3. Scroll vers la barre d'actions
          setTimeout(() => {
            stepActionsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 50);
        }}
      />

      {/* Actions contextuelles selon l'étape active (ou cliquée) */}
      <div ref={stepActionsRef}>
        <StepActionsBar
          event={event}
          requestsCount={requestsCount}
          confirmedCount={confirmedCount}
          totalAssignments={totalAssignments}
          overrideStep={viewStep}
          onResetOverride={() => setViewStep(null)}
          onOpenAvailRequest={() => setShowRequestModal(true)}
          onToggleLock={toggleAvailabilityLock}
          onImportExcel={() => setImportOpen(true)}
          onSwitchTab={setTab}
          onPublishAssignments={publishAssignments}
          onUnpublishAssignments={unpublishAssignments}
          onRemindUnconfirmed={remindUnconfirmed}
          onCloseEvent={closeEvent}
          onReopenEvent={reopenEvent}
          onPublishSchedule={publishEvent}
          onGoToPayroll={() => navigate("/paies")}
        />
      </div>

      <div className="mb-6">
        <div className="rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-4 px-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--surface-3)", border: "1px solid var(--border-strong)" }}>
              <Megaphone size={16} strokeWidth={2.2} className="text-[var(--text)]" />
            </div>
            <div>
              <div className="label mb-1">Diffusion des assignations</div>
              {event.assignments_published_at ? (
                <div className="text-sm text-[var(--text)]" data-testid="assignments-published-badge">
                  <span className="font-semibold">📢 Diffusées</span>
                  <span className="text-[var(--muted)]"> · {formatRelative(event.assignments_last_published_at || event.assignments_published_at)}</span>
                  {(() => {
                    const allDates = [...new Set((event.games || []).filter((g) => !g.is_break).map((g) => g.date).filter(Boolean))].sort();
                    const pubDates = (event.assignments_published_dates || []).slice().sort();
                    if (allDates.length > 1) {
                      const isFull = pubDates.length === allDates.length;
                      return (
                        <div className="text-xs text-[var(--muted)] mt-1">
                          {isFull
                            ? `Toutes les journées (${allDates.length}) sont diffusées`
                            : `${pubDates.length}/${allDates.length} journées diffusées : ${pubDates.join(", ")}`}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              ) : (
                <p className="text-sm text-[var(--muted)]" data-testid="assignments-pending-badge">
                  <span className="font-semibold text-[var(--text)]">○ Non diffusées</span>
                  <span> · Les officiels ne voient pas encore leurs parties</span>
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowPublishHistory(true)}
            className="btn-ghost shrink-0 text-xs"
            data-testid="open-publish-history-btn"
            title="Historique des diffusions SMS pour cet événement"
          >
            Historique
          </button>
        </div>
      </div>

      <div className="flex gap-8 border-b mb-8" style={{ borderColor: "var(--border)" }}>
        {[
          { id: "schedule", label: "Horaire" },
          { id: "availabilities", label: "Disponibilités" },
          { id: "summary", label: "Résumé" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`pb-3 text-sm font-medium transition-colors relative ${
              tab === t.id ? "tab-active font-semibold" : "text-[var(--muted)] hover:text-[var(--text)]"
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "schedule" ? (
        <ScheduleView
          event={event}
          officials={officials}
          updateGame={updateGame}
          removeGame={removeGame}
          addGame={addGame}
          cascadeFromGame={cascadeFromGame}
          openBreakModal={() => setShowBreakModal(true)}
          setEditing={setEditing}
          availIndex={availIndex}
          confirmIndex={confirmIndex}
          effectiveSlots={effectiveSlots}
          slotsForDate={slotsForDate}
          packages={settings?.packages || {}}
          clearSchedule={openClearModal}
          resetReferees={openResetRefsModal}
          selectionMode={selectionMode}
          selectedGameIds={selectedGameIds}
          onToggleSelectionMode={toggleSelectionMode}
          onToggleSelectGame={toggleSelectGame}
          onSelectAllGames={selectAllGames}
          onClearSelection={clearSelection}
          onDeleteSelectedGames={deleteSelectedGames}
          onApplyPackageToSelected={applyPackageToSelected}
        />
      ) : tab === "availabilities" ? (
        <AvailabilitiesTab eventId={event.id} event={event} officials={officials} onRefresh={() => refreshAvailabilities(event.id)} onEventChange={(e) => setEvent(e)} />
      ) : (
        <SummaryView
          event={event}
          officials={officials}
          grades={grades}
          settings={settings}
          extras={extras}
          onExtrasRefresh={() => refreshExtras(event.id)}
        />
      )}

      {editing && (
        <GameEditModal
          game={editing}
          event={event}
          officials={officials}
          availIndex={availIndex}
          slots={effectiveSlots}
          slotsForDate={slotsForDate}
          onClose={() => setEditing(null)}
          onSave={(patch, cascade) => {
            if (cascade) cascadeFromGame(editing.id, patch);
            else updateGame(editing.id, patch);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// =============== SCHEDULE VIEW ===============
function ScheduleView({
  event,
  officials,
  updateGame,
  removeGame,
  addGame,
  openBreakModal,
  setEditing,
  availIndex,
  confirmIndex,
  effectiveSlots,
  slotsForDate,
  packages = {},
  clearSchedule,
  resetReferees,
  selectionMode,
  selectedGameIds,
  onToggleSelectionMode,
  onToggleSelectGame,
  onSelectAllGames,
  onClearSelection,
  onDeleteSelectedGames,
  onApplyPackageToSelected,
}) {
  const conflicts = useMemo(() => findConflicts(event.games || []), [event.games]);
  const summary = summarizeConflicts(conflicts);
  const games = sortGames(event.games || []);
  const isMultiDay = new Set(games.map((g) => g.date).filter(Boolean)).size > 1;
  const playable = games.filter((g) => !g.is_break);
  const assigned = playable.filter((g) => g.referee1_id || g.referee2_id || g.scorer_id).length;
  const missingRefsCount = playable.filter((g) => isPackageIncomplete(g)).length;
  const [hideUnavailable, setHideUnavailable] = useState(true);
  const { sortKey: schedSortKey, sortDir: schedSortDir, cycleSort: schedCycleSort } = useSort(null);
  const sortGamesByCol = (rows) => {
    if (!schedSortKey) return rows;
    const get = (g) => {
      switch (schedSortKey) {
        case "time": return g.start_time || "";
        case "duration": return g.duration || 0;
        case "surface": return String(g.surface || "");
        case "category": return (g.category || "").toLowerCase();
        case "phase": return (g.phase_name || "").toLowerCase();
        case "teams": return `${g.team_a || ""} ${g.team_b || ""}`.toLowerCase();
        case "ref1": {
          const o = (officials || []).find((x) => x.id === g.referee1_id);
          return o ? `${o.first_name} ${o.last_name}`.toLowerCase() : "~";
        }
        case "ref2": {
          const o = (officials || []).find((x) => x.id === g.referee2_id);
          return o ? `${o.first_name} ${o.last_name}`.toLowerCase() : "~";
        }
        case "scorer": {
          const o = (officials || []).find((x) => x.id === g.scorer_id);
          return o ? `${o.first_name} ${o.last_name}`.toLowerCase() : "~";
        }
        default: return "";
      }
    };
    return [...rows].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (typeof va === "number") return schedSortDir === "asc" ? va - vb : vb - va;
      const cmp = String(va).localeCompare(String(vb), "fr", { sensitivity: "base" });
      return schedSortDir === "asc" ? cmp : -cmp;
    });
  };

  // ----- Filters -----
  const [filterCategory, setFilterCategory] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterSurface, setFilterSurface] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [filterOfficial, setFilterOfficial] = useState("");
  const [filterIncomplete, setFilterIncomplete] = useState(false);
  // Performance: show one day at a time by default when many games
  const [scheduleViewMode, setScheduleViewMode] = useState("single"); // "single" | "all"
  const [scheduleSelectedDay, setScheduleSelectedDay] = useState(null);

  const uniqueCategories = useMemo(
    () => [...new Set(playable.map((g) => (g.category || "").trim()).filter(Boolean))].sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event.games]
  );
  const uniqueDates = useMemo(
    () => [...new Set(games.map((g) => g.date).filter(Boolean))].sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event.games]
  );
  const uniqueSurfaces = useMemo(
    () => [...new Set(games.map((g) => String(g.surface)).filter(Boolean))].sort((a, b) => compareSurface(a, b)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event.games]
  );
  const uniqueTeams = useMemo(
    () => [...new Set(playable.flatMap((g) => [g.team_a, g.team_b]).map((t) => (t || "").trim()).filter(Boolean))].sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event.games]
  );

  // Liste des officiels effectivement assignés à au moins une partie de cet horaire.
  // Utilisée pour restreindre le filtre "Officiel" aux personnes concernées.
  const assignedOfficials = useMemo(() => {
    const ids = new Set();
    for (const g of playable) {
      if (g.referee1_id) ids.add(g.referee1_id);
      if (g.referee2_id) ids.add(g.referee2_id);
      if (g.scorer_id) ids.add(g.scorer_id);
    }
    return officials
      .filter((o) => ids.has(o.id))
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, "fr"));
  }, [playable, officials]);

  const filtersActive = !!(filterCategory || filterDate || filterSurface || filterTeam || filterOfficial || filterIncomplete);
  const matchesFilter = (g) => {
    if (filterDate && (g.date || "") !== filterDate) return false;
    if (filterSurface && String(g.surface) !== filterSurface) return false;
    if (filterCategory && (g.category || "").trim() !== filterCategory) return false;
    if (filterTeam) {
      const q = filterTeam.toLowerCase().trim();
      if (g.is_break) return false;
      const a = (g.team_a || "").toLowerCase();
      const b = (g.team_b || "").toLowerCase();
      if (!a.includes(q) && !b.includes(q)) return false;
    }
    if (filterOfficial) {
      if (g.is_break) return false;
      if (g.referee1_id !== filterOfficial && g.referee2_id !== filterOfficial && g.scorer_id !== filterOfficial) return false;
    }
    if (filterIncomplete) {
      if (g.is_break) return false;
      // Hide if the game IS complete (i.e. meets its forfait requirements)
      if (!isPackageIncomplete(g)) return false;
    }
    return true;
  };
  const filteredGames = filtersActive ? games.filter(matchesFilter) : games;
  const resetFilters = () => {
    setFilterCategory("");
    setFilterDate("");
    setFilterSurface("");
    setFilterTeam("");
    setFilterOfficial("");
    setFilterIncomplete(false);
  };

  // Confirmation status helper.
  // Returns: "confirmed" (explicit) | "available" (yes but not yet confirmed) | "declined" | "pending" | null
  const statusFor = (officialId, date, gameId, time) => {
    if (!officialId) return null;
    if (gameId && (confirmIndex || {})[officialId]?.has(gameId)) return "confirmed";
    if (!date) return null;
    const raw = (availIndex || {})[officialId]?.[date];
    if (raw == null) return "pending";
    // Use per-date slots when available, else event-level effective slots
    const slots = (typeof slotsForDate === "function" ? slotsForDate(date) : null) || effectiveSlots;
    const ans = time ? getSlotAnswer(raw, slotForTime(time, slots)) : (typeof raw === "string" ? raw : null);
    if (ans === "yes") return "available";
    if (ans === "no") return "declined";
    return "pending";
  };

  // Count declined assignments (indispo but still assigned)
  const declinedCount = playable.reduce((n, g) => {
    let c = 0;
    if (g.referee1_id && statusFor(g.referee1_id, g.date, g.id, g.start_time) === "declined") c++;
    if (g.referee2_id && statusFor(g.referee2_id, g.date, g.id, g.start_time) === "declined") c++;
    if (g.scorer_id && statusFor(g.scorer_id, g.date, g.id, g.start_time) === "declined") c++;
    return n + c;
  }, 0);

  const confirmedCount = playable.reduce((n, g) => {
    let c = 0;
    if (g.referee1_id && statusFor(g.referee1_id, g.date, g.id, g.start_time) === "confirmed") c++;
    if (g.referee2_id && statusFor(g.referee2_id, g.date, g.id, g.start_time) === "confirmed") c++;
    if (g.scorer_id && statusFor(g.scorer_id, g.date, g.id, g.start_time) === "confirmed") c++;
    return n + c;
  }, 0);
  const totalAssignments = playable.reduce((n, g) => n + (g.referee1_id ? 1 : 0) + (g.referee2_id ? 1 : 0) + (g.scorer_id ? 1 : 0), 0);

  return (
    <div>
      {/* Shared datalist for "Nom de partie" autocomplete (used by inline input + modal) */}
      <datalist id="phase-presets">
        <option value="Quart de finale" />
        <option value="Demi-finale" />
        <option value="Finale" />
        <option value="Bronze" />
        <option value="Ronde de classement" />
      </datalist>
      {(summary.total > 0 || summary.surface > 0 || summary.breaks > 0 || summary.duplicates > 0) && (
        <div className="mb-6 space-y-2">
          {summary.duplicates > 0 && (
            <ConflictBanner
              count={summary.duplicates}
              title={`${summary.duplicates} doublon${summary.duplicates > 1 ? "s" : ""} détecté${summary.duplicates > 1 ? "s" : ""}`}
              description="Plusieurs parties ont exactement les mêmes date, surface et heures. Lignes surlignées en rouge."
            />
          )}
          {summary.total > 0 && (
            <ConflictBanner
              count={summary.total}
              title={`${summary.total} conflit${summary.total > 1 ? "s" : ""} d'horaire détecté${summary.total > 1 ? "s" : ""}`}
              description={`${summary.officials} officiel${summary.officials > 1 ? "s sont" : " est"} en double-affectation. Les cellules en motif rayé indiquent le problème.`}
            />
          )}
          {summary.surface > 0 && (
            <ConflictBanner
              count={summary.surface}
              title={`${summary.surface} chevauchement${summary.surface > 1 ? "s" : ""} de surface`}
              description="Plusieurs parties sont planifiées sur la même surface en même temps."
            />
          )}
          {summary.breaks > 0 && (
            <ConflictBanner
              count={summary.breaks}
              title={`${summary.breaks} partie${summary.breaks > 1 ? "s" : ""} pendant une pause`}
              description="Une pause bloque cette surface au même moment."
            />
          )}
          {declinedCount > 0 && (
            <ConflictBanner
              count={declinedCount}
              title={`${declinedCount} affectation${declinedCount > 1 ? "s" : ""} sur officiel indispo`}
              description="Un ou plusieurs officiels assignés ont déclaré être INDISPONIBLES pour cette date. Réassigne ou modifie la date."
            />
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="text-sm text-[var(--text-2)]">
          <span className="text-[var(--text)] font-semibold tabular">{playable.length}</span> partie{playable.length > 1 ? "s" : ""} ·{" "}
          <span className="text-[var(--text)] font-semibold tabular">{assigned}</span> assignée{assigned > 1 ? "s" : ""}
          {totalAssignments > 0 && (
            <>
              {" "}· <span className="text-[var(--text)] font-semibold tabular">{confirmedCount}/{totalAssignments}</span> confirmation{confirmedCount > 1 ? "s" : ""}
            </>
          )}
          {games.filter((g) => g.is_break).length > 0 && (
            <>
              {" "}
              · <span className="text-[var(--text)] font-semibold tabular">{games.filter((g) => g.is_break).length}</span> pause
              {games.filter((g) => g.is_break).length > 1 ? "s" : ""}
            </>
          )}
          {" "}· Pause défaut : <span className="tabular">{event.break_between || 0}</span> min
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-colors" style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--text)" }} data-testid="hide-unavailable-toggle">
            <input
              type="checkbox"
              checked={hideUnavailable}
              onChange={(e) => setHideUnavailable(e.target.checked)}
              className="w-3.5 h-3.5 border-2 border-black"
            />
            <span className="font-bold text-[10px] uppercase tracking-widest">Masquer indispo</span>
          </label>
          <HelpHint
            title="Masquer indispo"
            text="Quand activé, le picker d'officiels cache automatiquement ceux qui ont répondu ✗ NON à la date de la partie. L'officiel actuellement assigné reste toujours visible même s'il est indispo."
            testid="hint-hide-unavailable"
          />
          <span className="inline-flex items-center">
            <button onClick={openBreakModal} className="btn-secondary" data-testid="add-break-btn">
              <Coffee size={14} strokeWidth={2.5} /> Pause
            </button>
            <HelpHint
              title="Ajouter une pause"
              text="Insère une pause dans l'horaire. Tu peux choisir si elle s'applique à toutes les surfaces ou à une seule. Pendant une pause, aucune partie ne peut être assignée à cette surface (conflit détecté)."
              testid="hint-add-break"
            />
          </span>
          <span className="inline-flex items-center">
            <button onClick={addGame} className="btn-primary" data-testid="add-game-btn">
              <Plus size={14} strokeWidth={2.5} /> Partie
            </button>
            <HelpHint
              title="Ajouter une partie"
              text="Insère une nouvelle ligne de partie à la fin de l'horaire avec heure auto-calculée (suit la dernière partie de la surface). Tu peux ensuite la modifier inline."
              testid="hint-add-game"
            />
          </span>
          {games.length > 0 && (
            <span className="inline-flex items-center">
              <button
                onClick={onToggleSelectionMode}
                className={selectionMode ? "btn-primary" : "btn-secondary"}
                data-testid="toggle-selection-mode-btn"
              >
                <CheckSquare size={14} strokeWidth={2.5} /> {selectionMode ? "Quitter sélection" : "Sélection..."}
              </button>
              <HelpHint
                title="Mode sélection multiple"
                text="Active des cases à cocher devant chaque partie pour en sélectionner plusieurs (ex: parties 1, 4, 7, 10, 13). Tu peux ensuite les effacer d'un coup avec le bouton qui apparaît en bas de l'écran."
                testid="hint-selection-mode"
              />
            </span>
          )}
          {games.length > 0 && (
            <span className="inline-flex items-center">
              <button
                onClick={resetReferees}
                className="btn-secondary"
                data-testid="reset-referees-btn"
              >
                <RefreshCcw size={14} strokeWidth={2.5} /> Réinit. arbitres...
              </button>
              <HelpHint
                title="Réinitialiser les arbitres assignés"
                text="Ouvre un modal pour vider les Arbitre 1 et Arbitre 2 sur tout l'horaire OU sur les parties d'une ou plusieurs date(s) / surface(s) précises. Les marqueurs sont préservés. Utile quand tu refais les assignations d'une journée."
                testid="hint-reset-refs"
              />
            </span>
          )}
          {games.length > 0 && (
            <span className="inline-flex items-center">
              <button
                onClick={clearSchedule}
                className="btn-secondary"
                data-testid="clear-schedule-btn"
              >
                <Trash2 size={14} strokeWidth={2.5} /> Effacer...
              </button>
              <HelpHint
                title="Effacer des parties"
                text="Ouvre un modal pour effacer soit TOUT l'horaire, soit seulement les parties d'une ou plusieurs date(s) spécifiques, ou d'une ou plusieurs surface(s). Tu peux combiner date + surface (ex: « toutes les parties du 2 juin sur la patinoire 1 »). Aperçu en temps réel du nombre de parties affectées."
                testid="hint-clear-schedule"
              />
            </span>
          )}
        </div>
      </div>

      {totalAssignments > 0 && (
        <div className="flex flex-wrap items-center gap-4 mb-3 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
          <span>Statut des affectations :</span>
          <span className="inline-flex items-center gap-1.5"><StatusBadge status="confirmed" /> Confirmé</span>
          <span className="inline-flex items-center gap-1.5"><StatusBadge status="available" /> Disponible</span>
          <span className="inline-flex items-center gap-1.5"><StatusBadge status="declined" /> Indispo</span>
          <span className="inline-flex items-center gap-1.5"><StatusBadge status="pending" /> Sans réponse</span>
        </div>
      )}

      {(conflicts.gamesInDuplicate.size > 0 || conflicts.gamesInSurfaceConflict.size > 0 || conflicts.gamesInBreakConflict.size > 0 || conflicts.gamesInOfficialConflict.size > 0 || missingRefsCount > 0) && (
        <div className="flex flex-wrap items-center gap-4 mb-3 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
          <span>Légende horaire :</span>
          {conflicts.gamesInDuplicate.size > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 h-3 border border-black" style={{ backgroundColor: "#fecaca" }} />
              Doublon ({conflicts.gamesInDuplicate.size})
            </span>
          )}
          {conflicts.gamesInOfficialConflict.size > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 h-3 border border-black" style={{ backgroundColor: "#f5d0fe" }} />
              Chevauch. arbitre ({conflicts.gamesInOfficialConflict.size})
            </span>
          )}
          {(conflicts.gamesInSurfaceConflict.size + conflicts.gamesInBreakConflict.size) > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 h-3 border border-black" style={{ backgroundColor: "#fde68a" }} />
              Chevauch. surface ({conflicts.gamesInSurfaceConflict.size + conflicts.gamesInBreakConflict.size})
            </span>
          )}
          {missingRefsCount > 0 && (
            <button
              type="button"
              onClick={() => setFilterIncomplete((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 border border-black transition-colors ${filterIncomplete ? "bg-black text-white" : "hover:bg-[var(--surface-3)]"}`}
              data-testid="legend-missing-refs"
              title={filterIncomplete ? "Désactiver le filtre" : "Voir uniquement les parties incomplètes"}
            >
              <span className="inline-block w-4 h-3 border border-black" style={{ backgroundColor: "#dbeafe" }} />
              Arbitres manquants ({missingRefsCount}){filterIncomplete ? " · actif" : ""}
            </button>
          )}
        </div>
      )}

      {/* Filters bar */}
      {(uniqueDates.length > 0 || uniqueSurfaces.length > 0 || uniqueCategories.length > 0 || uniqueTeams.length > 0) && (
        <div className="card-flat !p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="schedule-filters">
          {isMultiDay && (
            <div className="flex-1 min-w-[140px]">
              <label className="label block mb-1">Date</label>
              <select
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="input-box text-xs py-1.5"
                data-testid="filter-date"
              >
                <option value="">Toutes</option>
                {uniqueDates.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex-1 min-w-[140px]">
            <label className="label block mb-1">Emplacement</label>
            <select
              value={filterSurface}
              onChange={(e) => setFilterSurface(e.target.value)}
              className="input-box text-xs py-1.5"
              data-testid="filter-surface"
            >
              <option value="">Tous</option>
              {uniqueSurfaces.map((s) => (
                <option key={s} value={s}>{formatSurface(s)}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="label block mb-1">Catégorie</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="input-box text-xs py-1.5"
              data-testid="filter-category"
            >
              <option value="">Toutes</option>
              {uniqueCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="label block mb-1">Équipe</label>
            <input
              type="text"
              value={filterTeam}
              onChange={(e) => setFilterTeam(e.target.value)}
              placeholder="Recherche équipe…"
              list="schedule-team-list"
              className="input-box text-xs py-1.5"
              data-testid="filter-team"
            />
            <datalist id="schedule-team-list">
              {uniqueTeams.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="label block mb-1">Officiel</label>
            <select
              value={filterOfficial}
              onChange={(e) => setFilterOfficial(e.target.value)}
              className="input-box text-xs py-1.5"
              data-testid="filter-official"
            >
              <option value="">Tous ({assignedOfficials.length} assigné{assignedOfficials.length > 1 ? "s" : ""})</option>
              {assignedOfficials.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.first_name} {o.last_name}
                </option>
              ))}
              {assignedOfficials.length === 0 && (
                <option value="" disabled>Aucun officiel assigné pour l&apos;instant</option>
              )}
            </select>
          </div>
          {filtersActive && (
            <button
              onClick={resetFilters}
              className="btn-ghost shrink-0"
              data-testid="reset-filters-btn"
              title="Réinitialiser les filtres"
            >
              <X size={14} strokeWidth={2.5} /> Réinitialiser
            </button>
          )}
        </div>
      )}

      {filtersActive && (
        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mb-3" data-testid="filter-summary">
          <span className="text-black font-bold tabular">{filteredGames.filter((g) => !g.is_break).length}</span> partie{filteredGames.filter((g) => !g.is_break).length > 1 ? "s" : ""} affichée{filteredGames.filter((g) => !g.is_break).length > 1 ? "s" : ""} sur {playable.length}
        </div>
      )}

      {(() => {
        // Group games by date when multi-day; otherwise single group
        const groups = {};
        filteredGames.forEach((g) => {
          const k = g.date || "";
          (groups[k] = groups[k] || []).push(g);
        });
        const dateKeys = Object.keys(groups).sort();
        // Auto-init selected day on first render
        if (isMultiDay && scheduleSelectedDay === null && dateKeys.length > 0) {
          // Use setTimeout to avoid setState in render warning
          setTimeout(() => setScheduleSelectedDay(dateKeys[0]), 0);
        }
        // Filter visible days according to view mode
        const visibleDateKeys = (isMultiDay && scheduleViewMode === "single" && scheduleSelectedDay)
          ? dateKeys.filter((dk) => dk === scheduleSelectedDay)
          : dateKeys;
        const renderTable = (dayGames) => (
          <div className="card-flat !p-0 overflow-x-auto" key={dayGames[0]?.id || "empty"}>
            <table className="w-full text-xs" data-testid="schedule-table">
              <thead className="sticky-th">
                <tr className="border-b-2 border-black">
                  {selectionMode && (
                    <Th>
                      <input
                        type="checkbox"
                        checked={dayGames.length > 0 && dayGames.every((g) => selectedGameIds.has(g.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const dayIds = dayGames.map((g) => g.id);
                            onSelectAllGames([...selectedGameIds, ...dayIds]);
                          } else {
                            const dayIdsSet = new Set(dayGames.map((g) => g.id));
                            onSelectAllGames([...selectedGameIds].filter((id) => !dayIdsSet.has(id)));
                          }
                        }}
                        className="w-3.5 h-3.5 cursor-pointer"
                        data-testid={`select-all-day-${dayGames[0]?.id || "empty"}`}
                        title="Tout sélectionner sur cette journée"
                      />
                    </Th>
                  )}
                  <SortHeader k="time" sortKey={schedSortKey} sortDir={schedSortDir} onSort={schedCycleSort}>Heure</SortHeader>
                  <SortHeader k="duration" sortKey={schedSortKey} sortDir={schedSortDir} onSort={schedCycleSort}>Durée</SortHeader>
                  <SortHeader k="surface" sortKey={schedSortKey} sortDir={schedSortDir} onSort={schedCycleSort}>Surf.</SortHeader>
                  <SortHeader k="category" sortKey={schedSortKey} sortDir={schedSortDir} onSort={schedCycleSort}>Catégorie</SortHeader>
                  <SortHeader k="phase" sortKey={schedSortKey} sortDir={schedSortDir} onSort={schedCycleSort}>Nom de partie</SortHeader>
                  <SortHeader k="teams" sortKey={schedSortKey} sortDir={schedSortDir} onSort={schedCycleSort}>Équipes</SortHeader>
                  <SortHeader k="ref1" sortKey={schedSortKey} sortDir={schedSortDir} onSort={schedCycleSort}>Arbitre 1</SortHeader>
                  <SortHeader k="ref2" sortKey={schedSortKey} sortDir={schedSortDir} onSort={schedCycleSort}>Arbitre 2</SortHeader>
                  <SortHeader k="scorer" sortKey={schedSortKey} sortDir={schedSortDir} onSort={schedCycleSort}>Marqueur</SortHeader>
                  <Th>Forfait</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {sortGamesByCol(dayGames).map((g) => {
                  const isDup = conflicts.gamesInDuplicate.has(g.id);
                  const isOfficialConflict = !isDup && conflicts.gamesInOfficialConflict.has(g.id);
                  const isOverlap = !isDup && !isOfficialConflict && (conflicts.gamesInSurfaceConflict.has(g.id) || conflicts.gamesInBreakConflict.has(g.id));
                  // Une ligne est incomplète si les assignations ne couvrent pas
                  // les exigences du forfait choisi (ex: forfait C = 1 arbitre + 1 marqueur).
                  // Sans override, l'attente par défaut est forfait D (2 arbitres + marqueur).
                  const isMissingRefs = !g.is_break && !isDup && !isOfficialConflict && !isOverlap && isPackageIncomplete(g);
                  const rowStyle = isDup
                    ? { backgroundColor: "#fecaca" }
                    : isOfficialConflict
                    ? { backgroundColor: "#f5d0fe" }
                    : isOverlap
                    ? { backgroundColor: "#fde68a" }
                    : isMissingRefs
                    ? { backgroundColor: "#dbeafe" }
                    : undefined;
                  return g.is_break ? (
                    <tr key={g.id} className="break-row" data-testid={`game-row-${g.id}`}>
                      {selectionMode && (
                        <Td>
                          <input
                            type="checkbox"
                            checked={selectedGameIds.has(g.id)}
                            onChange={() => onToggleSelectGame(g.id)}
                            className="w-3.5 h-3.5 cursor-pointer"
                            data-testid={`select-game-${g.id}`}
                          />
                        </Td>
                      )}
                      <Td>{g.start_time}→{g.end_time}</Td>
                      <Td>{g.duration} min</Td>
                      <Td>{formatSurface(g.surface)}</Td>
                      <Td colSpan={7}>
                        <span className="badge-dark inline-flex items-center gap-1 mr-2">⏸ Pause</span>
                        <input
                          value={g.break_label || ""}
                          onChange={(e) => updateGame(g.id, { break_label: e.target.value })}
                          placeholder="Description"
                          className="bg-transparent border-b border-white/30 focus:border-white outline-none text-xs px-1 text-white placeholder-white/50"
                        />
                      </Td>
                      <Td>
                        <button onClick={() => setEditing(g)} className="text-white/70 hover:text-white mr-2" title="Modifier">
                          <Edit3 size={13} strokeWidth={2.5} />
                        </button>
                        <button onClick={() => removeGame(g)} className="text-white/70 hover:text-white" title="Supprimer">
                          <Trash2 size={13} strokeWidth={2.5} />
                        </button>
                      </Td>
                    </tr>
                  ) : (
                    <tr
                      key={g.id}
                      className={`transition-colors ${!rowStyle ? "hover:bg-[var(--surface-3)]" : ""}`}
                      style={rowStyle || { borderTop: "1px solid var(--border)" }}
                      data-testid={`game-row-${g.id}`}
                    >
                      {selectionMode && (
                        <Td>
                          <input
                            type="checkbox"
                            checked={selectedGameIds.has(g.id)}
                            onChange={() => onToggleSelectGame(g.id)}
                            className="w-3.5 h-3.5 cursor-pointer"
                            data-testid={`select-game-${g.id}`}
                          />
                        </Td>
                      )}
                      <Td>
                        <span className="inline-flex items-center gap-1">
                          {(isDup || isOfficialConflict || isOverlap) && <AlertTriangle size={12} strokeWidth={3} />}
                          <span className="tabular">{g.start_time}→{g.end_time}</span>
                        </span>
                      </Td>
                      <Td><span className="tabular">{g.duration || computeDuration(g.start_time, g.end_time)} min</span></Td>
                      <Td>{formatSurface(g.surface)}</Td>
                      <Td>
                        <input
                          value={g.category || ""}
                          onChange={(e) => updateGame(g.id, { category: e.target.value })}
                          placeholder="U13 A…"
                          className="bg-transparent input-line !py-1 !text-xs w-20"
                        />
                      </Td>
                      <Td>
                        <input
                          value={g.phase_name || ""}
                          onChange={(e) => updateGame(g.id, { phase_name: e.target.value })}
                          placeholder="—"
                          list="phase-presets"
                          className="bg-transparent input-line !py-1 !text-xs w-28"
                          data-testid={`game-phase-${g.id}`}
                        />
                      </Td>
                      <Td>
                        <div className="flex items-center gap-1">
                          <input
                            value={g.team_a || ""}
                            onChange={(e) => updateGame(g.id, { team_a: e.target.value })}
                            placeholder="A"
                            className="bg-transparent input-line !py-1 !text-xs w-16"
                          />
                          <span className="text-[var(--muted)] text-[10px]">vs</span>
                          <input
                            value={g.team_b || ""}
                            onChange={(e) => updateGame(g.id, { team_b: e.target.value })}
                            placeholder="B"
                            className="bg-transparent input-line !py-1 !text-xs w-16"
                          />
                        </div>
                      </Td>
                      <Td>
                        <OfficialPicker
                          value={g.referee1_id}
                          onChange={(v) => updateGame(g.id, { referee1_id: v })}
                          officials={officials}
                          role="referee"
                          isConflict={!!conflicts.conflictsByGameRole[`${g.id}|referee1_id`]}
                          status={statusFor(g.referee1_id, g.date, g.id, g.start_time)}
                          date={g.date}
                          time={g.start_time}
                          slots={slotsForDate ? slotsForDate(g.date) : effectiveSlots}
                          availIndex={availIndex}
                          hideUnavailable={hideUnavailable}
                        />
                      </Td>
                      <Td>
                        <OfficialPicker
                          value={g.referee2_id}
                          onChange={(v) => updateGame(g.id, { referee2_id: v })}
                          officials={officials}
                          role="referee"
                          isConflict={!!conflicts.conflictsByGameRole[`${g.id}|referee2_id`]}
                          status={statusFor(g.referee2_id, g.date, g.id, g.start_time)}
                          date={g.date}
                          time={g.start_time}
                          slots={slotsForDate ? slotsForDate(g.date) : effectiveSlots}
                          availIndex={availIndex}
                          hideUnavailable={hideUnavailable}
                        />
                      </Td>
                      <Td>
                        <OfficialPicker
                          value={g.scorer_id}
                          onChange={(v) => updateGame(g.id, { scorer_id: v })}
                          officials={officials}
                          role="scorer"
                          isConflict={!!conflicts.conflictsByGameRole[`${g.id}|scorer_id`]}
                          status={statusFor(g.scorer_id, g.date, g.id, g.start_time)}
                          date={g.date}
                          time={g.start_time}
                          slots={slotsForDate ? slotsForDate(g.date) : effectiveSlots}
                          availIndex={availIndex}
                          hideUnavailable={hideUnavailable}
                        />
                      </Td>
                      <Td>
                        <PackagePicker
                          game={g}
                          packages={packages}
                          onChange={(v) => updateGame(g.id, { package_override: v })}
                        />
                      </Td>
                      <Td>
                        <button
                          onClick={() => setEditing(g)}
                          className="text-[var(--muted)] hover:text-black mr-2"
                          title="Modifier"
                          data-testid={`edit-game-${g.id}`}
                        >
                          <Edit3 size={13} strokeWidth={2.5} />
                        </button>
                        <button
                          onClick={() => removeGame(g)}
                          className="text-[var(--muted)] hover:text-black"
                          title="Supprimer"
                          data-testid={`delete-game-${g.id}`}
                        >
                          <Trash2 size={13} strokeWidth={2.5} />
                        </button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );

        if (!isMultiDay) {
          if (filteredGames.length === 0 && filtersActive) {
            return (
              <div className="border-2 border-dashed border-black p-8 text-center" data-testid="filter-empty">
                <p className="font-bold text-sm mb-1">Aucune partie ne correspond aux filtres</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Modifie ou réinitialise les filtres pour voir les autres parties.</p>
              </div>
            );
          }
          return renderTable(filteredGames);
        }
        if (dateKeys.length === 0 && filtersActive) {
          return (
            <div className="border-2 border-dashed border-black p-8 text-center" data-testid="filter-empty">
              <p className="font-bold text-sm mb-1">Aucune partie ne correspond aux filtres</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Modifie ou réinitialise les filtres pour voir les autres parties.</p>
            </div>
          );
        }
        return (
          <div className="space-y-8">
            {/* Day selector tabs (multi-day events) */}
            {isMultiDay && (
              <div className="card-flat !p-3 flex flex-wrap items-center gap-2" data-testid="day-tabs">
                <span className="label mr-2">Voir :</span>
                {dateKeys.map((dk) => {
                  const dayCount = groups[dk].filter((g) => !g.is_break).length;
                  const isActive = scheduleViewMode === "single" && scheduleSelectedDay === dk;
                  return (
                    <button
                      key={dk || "no-date"}
                      onClick={() => { setScheduleViewMode("single"); setScheduleSelectedDay(dk); }}
                      className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider border tabular ${
                        isActive
                          ? "bg-black text-white border-black"
                          : "border-[var(--border-strong)] text-[var(--muted)] hover:border-black hover:text-black"
                      }`}
                      data-testid={`day-tab-${dk || "no-date"}`}
                    >
                      {formatDateLong(dk)} <span className="opacity-70">({dayCount})</span>
                    </button>
                  );
                })}
                <button
                  onClick={() => setScheduleViewMode("all")}
                  className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider border ${
                    scheduleViewMode === "all"
                      ? "bg-black text-white border-black"
                      : "border-[var(--border-strong)] text-[var(--muted)] hover:border-black hover:text-black"
                  }`}
                  data-testid="day-tab-all"
                >
                  Tous {playable.length > 200 ? "⚠ lent" : ""}
                </button>
              </div>
            )}
            {visibleDateKeys.map((dk) => {
              const dayGames = groups[dk];
              const dayPlayable = dayGames.filter((g) => !g.is_break);
              const dayDups = dayGames.filter((g) => conflicts.gamesInDuplicate.has(g.id)).length;
              const dayOfficial = dayGames.filter((g) => !conflicts.gamesInDuplicate.has(g.id) && conflicts.gamesInOfficialConflict.has(g.id)).length;
              const dayOverlaps = dayGames.filter((g) => !conflicts.gamesInDuplicate.has(g.id) && !conflicts.gamesInOfficialConflict.has(g.id) && (conflicts.gamesInSurfaceConflict.has(g.id) || conflicts.gamesInBreakConflict.has(g.id))).length;
              return (
                <div key={dk || "no-date"} data-testid={`day-section-${dk || "no-date"}`}>
                  <div className="flex items-end justify-between mb-3 border-b-2 border-black pb-1.5">
                    <div>
                      <div className="label">Journée</div>
                      <h3 className="font-serif text-2xl leading-tight tabular">{formatDateLong(dk)}</h3>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] flex flex-wrap gap-3 items-center">
                      <span><span className="text-black font-bold tabular">{dayPlayable.length}</span> partie{dayPlayable.length > 1 ? "s" : ""}</span>
                      {dayDups > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-black" style={{ backgroundColor: "#fecaca" }}>
                          <span className="text-black font-bold tabular">{dayDups}</span> doublon{dayDups > 1 ? "s" : ""}
                        </span>
                      )}
                      {dayOfficial > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-black" style={{ backgroundColor: "#f5d0fe" }}>
                          <span className="text-black font-bold tabular">{dayOfficial}</span> arbitre{dayOfficial > 1 ? "s" : ""}
                        </span>
                      )}
                      {dayOverlaps > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-black" style={{ backgroundColor: "#fde68a" }}>
                          <span className="text-black font-bold tabular">{dayOverlaps}</span> chevauch.
                        </span>
                      )}
                    </div>
                  </div>
                  {renderTable(dayGames)}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Floating selection bar */}
      {selectionMode && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3"
          style={{ background: "var(--text)", color: "var(--surface)" }}
          data-testid="selection-bar"
        >
          <span className="text-sm font-semibold tabular">
            {selectedGameIds.size} partie{selectedGameIds.size > 1 ? "s" : ""} sélectionnée{selectedGameIds.size > 1 ? "s" : ""}
          </span>
          <span className="opacity-30">|</span>
          <button
            onClick={() => onSelectAllGames(games.filter((g) => !g.is_break).map((g) => g.id))}
            className="text-xs underline hover:opacity-80"
            data-testid="select-all-btn"
          >
            Tout sélectionner
          </button>
          <button
            onClick={onClearSelection}
            className="text-xs underline hover:opacity-80"
            disabled={selectedGameIds.size === 0}
            data-testid="select-none-btn"
          >
            Aucun
          </button>
          <span className="opacity-30">|</span>
          <div className="flex items-center gap-1.5" data-testid="bulk-package-group">
            <span className="text-xs opacity-70 font-mono uppercase tracking-wider">Forfait :</span>
            {["A", "B", "C", "D"].map((p) => (
              <button
                key={p}
                onClick={() => onApplyPackageToSelected && onApplyPackageToSelected(p)}
                disabled={selectedGameIds.size === 0}
                className="px-2 py-1 rounded-md text-xs font-mono font-bold tabular border border-white/30 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`bulk-package-${p}`}
                title={`Appliquer forfait ${p} à toutes les parties sélectionnées`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => onApplyPackageToSelected && onApplyPackageToSelected(null)}
              disabled={selectedGameIds.size === 0}
              className="px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="bulk-package-auto"
              title="Réinitialiser au forfait Auto-détecté"
            >
              Auto
            </button>
          </div>
          <span className="opacity-30">|</span>
          <button
            onClick={onDeleteSelectedGames}
            disabled={selectedGameIds.size === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "var(--surface)", color: "var(--text)" }}
            data-testid="delete-selected-btn"
          >
            <Trash2 size={12} strokeWidth={2.5} /> Effacer
          </button>
          <button
            onClick={onToggleSelectionMode}
            className="text-xs underline hover:opacity-80"
            data-testid="exit-selection-btn"
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}

function formatDateLong(iso) {
  if (!iso) return "Sans date";
  try {
    const d = new Date(`${iso}T12:00:00`);
    if (isNaN(d)) return iso;
    const s = d.toLocaleDateString("fr-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch {
    return iso;
  }
}

function Th({ children }) {
  return <th className="py-3 px-3 text-left font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] whitespace-nowrap">{children}</th>;
}
function Td({ children, colSpan }) {
  return (
    <td colSpan={colSpan} className="py-2.5 px-3 font-mono text-xs whitespace-nowrap">
      {children}
    </td>
  );
}

function ConflictBanner({ count, title, description }) {
  return (
    <div className="border-double-thick bg-conflict-stripes-light p-4 flex items-start gap-3 shake-once" data-testid="conflict-banner">
      <div className="shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold tabular">{count}</div>
      <div className="flex-1">
        <div className="font-bold text-sm uppercase tracking-wider mb-0.5">{title}</div>
        <div className="text-xs text-[var(--text)]">{description}</div>
      </div>
    </div>
  );
}

function OfficialPicker({ value, onChange, officials, role, isConflict, status, date, time, slots, availIndex, hideUnavailable }) {
  // Resolve a slot-aware "yes"|"no"|null answer for a given official on this game.
  const resolve = (officialId) => {
    const raw = availIndex?.[officialId]?.[date];
    if (raw == null) return null;
    if (time && slots) return getSlotAnswer(raw, slotForTime(time, slots));
    if (typeof raw === "string") return raw;
    return null;
  };

  // Filtrage STRICT par rôle : seuls les officiels qui ont ce rôle dans leur liste apparaissent.
  // (les comptes legacy sans `roles` ont été backfillés au startup avec ['referee','scorer'])
  // Fix Feb 18, 2026 : si `roles` est manquant/vide côté client (avant que la
  // migration serveur ait tourné, ou après import Excel non-normalisé), on
  // inclut l'officiel dans TOUS les rôles au lieu de le masquer.
  let filtered = officials.filter((o) => {
    if (o.id === value) return true; // always keep current assignment visible
    const roles = o.roles;
    if (!Array.isArray(roles) || roles.length === 0) return true;
    return roles.includes(role);
  });

  if (hideUnavailable && date && availIndex) {
    filtered = filtered.filter((o) => {
      if (o.id === value) return true; // always show currently assigned
      const ans = resolve(o.id);
      return ans !== "no" && ans !== "maybe";
    });
  }

  // Tri : ceux qui ont le grade du rôle d'abord, puis les autres
  const hasRoleGrade = (o) => (role === "referee" ? !!o.referee_grade_id : role === "scorer" ? !!o.scorer_grade_id : true);

  // Groupe par réponse pour la date courante : Disponibles d'abord, puis Sans réponse, puis Indispos
  const groups = { yes: [], pending: [], no: [] };
  filtered.forEach((o) => {
    const ans = date ? resolve(o.id) : null;
    if (ans === "yes") groups.yes.push(o);
    else if (ans === "no") groups.no.push(o);
    else groups.pending.push(o);
  });
  // Dans chaque groupe, met les "bon grade" en premier
  for (const k of Object.keys(groups)) {
    groups[k].sort((a, b) => {
      const ga = hasRoleGrade(a) ? 0 : 1;
      const gb = hasRoleGrade(b) ? 0 : 1;
      if (ga !== gb) return ga - gb;
      return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, "fr", { sensitivity: "base" });
    });
  }

  const renderOption = (o, availSuffix) => {
    const noGrade = !hasRoleGrade(o);
    const gradeSuffix = noGrade ? " · sans grade" : "";
    return (
      <option key={o.id} value={o.id}>
        {o.first_name} {o.last_name}{availSuffix}{gradeSuffix}
      </option>
    );
  };

  return (
    <div className="flex items-center gap-1.5">
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className={`text-xs px-2.5 py-1.5 rounded-lg outline-none min-w-[120px] transition-colors ${
          isConflict ? "font-semibold" : ""
        }`}
        style={{
          background: isConflict ? "rgba(248, 113, 113, 0.10)" : "var(--surface)",
          border: `1px solid ${isConflict ? "var(--danger)" : "var(--border-strong)"}`,
          color: "var(--text)",
        }}
        data-testid={`picker-${role}`}
      >
        <option value="">— non assigné —</option>
        {date ? (
          <>
            {groups.yes.length > 0 && (
              <optgroup label="✓ Disponibles">
                {groups.yes.map((o) => renderOption(o, " ○"))}
              </optgroup>
            )}
            {groups.pending.length > 0 && (
              <optgroup label="? Sans réponse">
                {groups.pending.map((o) => renderOption(o, ""))}
              </optgroup>
            )}
            {groups.no.length > 0 && (
              <optgroup label="✗ Indisponibles">
                {groups.no.map((o) => renderOption(o, " ✗"))}
              </optgroup>
            )}
          </>
        ) : (
          filtered.map((o) => renderOption(o, ""))
        )}
      </select>
      {value && status && <StatusBadge status={status} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    confirmed: { label: "✓", title: "Confirmé — l'officiel a confirmé son assignation", cls: "text-black border-black", style: { backgroundColor: "#39ff14" } },
    available: { label: "○", title: "Disponible — a répondu OUI à la demande, mais n'a pas encore confirmé l'assignation", cls: "bg-white text-black border-black border-dashed", style: undefined },
    declined: { label: "✗", title: "INDISPO — a répondu NON", cls: "bg-conflict-stripes-light text-black border-2 border-black border-double-thick", style: undefined },
    pending: { label: "?", title: "Sans réponse — pas de demande envoyée ou pas encore répondu", cls: "bg-white text-[#a3a3a3] border-[#a3a3a3]", style: undefined },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <span
      title={m.title}
      style={m.style}
      className={`inline-flex items-center justify-center w-5 h-5 border font-mono text-[10px] font-bold shrink-0 ${m.cls}`}
      data-testid={`status-${status}`}
    >
      {m.label}
    </span>
  );
}

// =============== SUMMARY VIEW ===============
// Extracted to /components/event/SummaryView.jsx (Feb 27, 2026)

// =============== GAME EDIT MODAL ===============
function GameEditModal({ game, event, officials = [], availIndex = {}, slots, slotsForDate, onClose, onSave }) {
  // Resolve slots based on the date of the game being edited (uses per-date override if any)
  const effSlots = (typeof slotsForDate === "function" ? slotsForDate(game.date) : null) || slots || DEFAULT_SLOTS;
  const [f, setF] = useState({
    date: game.date || "",
    start_time: game.start_time,
    duration: game.duration || computeDuration(game.start_time, game.end_time),
    surface: game.surface,
    category: game.category || "",
    phase_name: game.phase_name || "",
    team_a: game.team_a || "",
    team_b: game.team_b || "",
    referee1_id: game.referee1_id || null,
    referee2_id: game.referee2_id || null,
    scorer_id: game.scorer_id || null,
    is_break: !!game.is_break,
    break_label: game.break_label || "",
  });

  const newEnd = addMinutes(f.start_time, parseInt(f.duration) || 0);

  // Disponibles dates : on prend l'union des dates des parties + date courante
  const availableDates = useMemo(() => {
    const ds = new Set((event.games || []).map((g) => g.date).filter(Boolean));
    if (f.date) ds.add(f.date);
    if (game.date) ds.add(game.date);
    return [...ds].sort();
  }, [event.games, f.date, game.date]);

  const submit = () => {
    const patch = {
      date: f.date || "",
      start_time: f.start_time,
      end_time: newEnd,
      duration: parseInt(f.duration) || 0,
      surface: String(f.surface),
      category: f.category || "",
      phase_name: f.phase_name || "",
      team_a: f.team_a || "",
      team_b: f.team_b || "",
      referee1_id: f.referee1_id || null,
      referee2_id: f.referee2_id || null,
      scorer_id: f.scorer_id || null,
      is_break: f.is_break,
      break_label: f.is_break ? f.break_label || "Pause" : "",
    };
    if (f.is_break) {
      patch.team_a = "";
      patch.team_b = "";
      patch.category = "";
      patch.phase_name = "";
      patch.referee1_id = null;
      patch.referee2_id = null;
      patch.scorer_id = null;
    }
    // Cascade automatique désactivée — l'édition d'une partie ne décale plus les suivantes.
    // Le décalage explicite reste disponible via le bouton dédié dans la grille horaire.
    onSave(patch, false);
  };

  const surfaces = Array.from({ length: event.surfaces || 4 }, (_, i) => String(i + 1));
  const existing = [...new Set(event.games.map((g) => String(g.surface)))];
  const surfacesOptions = [...new Set([...surfaces, ...existing])].sort((a, b) => compareSurface(a, b));

  return (
    <div className="fixed inset-0 z-[90] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-6 max-w-2xl w-full shadow-2xl my-8" data-testid="game-edit-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="label">Modifier {f.is_break ? "pause" : "partie"}</div>
            <h3 className="font-serif text-3xl leading-tight">
              {f.is_break ? f.break_label || "Pause" : `${f.team_a || "Équipe A"} vs ${f.team_b || "Équipe B"}`}
            </h3>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-black" data-testid="game-edit-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="label mb-2">Type</div>
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button
            type="button"
            onClick={() => setF({ ...f, is_break: false })}
            className={`p-3 border text-sm ${!f.is_break ? "border-black bg-black text-white" : "border-[var(--border-strong)] text-[var(--muted)]"}`}
            data-testid="game-edit-type-game"
          >
            Partie
          </button>
          <button
            type="button"
            onClick={() => setF({ ...f, is_break: true, break_label: f.break_label || "Pause" })}
            className={`p-3 border text-sm ${f.is_break ? "border-black bg-black text-white" : "border-[var(--border-strong)] text-[var(--muted)]"}`}
            data-testid="game-edit-type-break"
          >
            Pause
          </button>
        </div>

        {f.is_break && (
          <div className="mb-4">
            <label className="label block mb-1.5">Description de la pause</label>
            <input
              value={f.break_label}
              onChange={(e) => setF({ ...f, break_label: e.target.value })}
              className="input-box"
              placeholder="Pause repas, cérémonie…"
              data-testid="game-edit-break-label"
            />
          </div>
        )}

        {/* Date / Heure / Durée / Surface */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="label block mb-1.5">Date</label>
            {availableDates.length > 1 ? (
              <select
                value={f.date || ""}
                onChange={(e) => setF({ ...f, date: e.target.value })}
                className="input-box"
                data-testid="game-edit-date"
              >
                {!availableDates.includes(f.date) && <option value={f.date || ""}>{f.date || "—"}</option>}
                {availableDates.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            ) : (
              <input
                type="date"
                value={f.date || ""}
                onChange={(e) => setF({ ...f, date: e.target.value })}
                className="input-box"
                data-testid="game-edit-date"
              />
            )}
          </div>
          <div>
            <label className="label block mb-1.5">Heure début</label>
            <input
              type="time"
              value={f.start_time}
              onChange={(e) => setF({ ...f, start_time: e.target.value })}
              className="input-box"
              data-testid="game-edit-start"
            />
          </div>
          <div>
            <label className="label block mb-1.5">Durée (min)</label>
            <input
              type="number"
              min="1"
              value={f.duration}
              onChange={(e) => setF({ ...f, duration: e.target.value })}
              className="input-box"
              data-testid="game-edit-duration"
            />
          </div>
          <div>
            <label className="label block mb-1.5">Surface</label>
            <select
              value={String(f.surface)}
              onChange={(e) => setF({ ...f, surface: e.target.value })}
              className="input-box"
              data-testid="game-edit-surface"
            >
              {surfacesOptions.map((s) => (
                <option key={s} value={s}>#{s}</option>
              ))}
            </select>
          </div>
        </div>

        <p className="text-xs text-[var(--muted)] mb-4">
          Fin calculée : <span className="text-black font-bold tabular">{newEnd}</span>
        </p>

        {!f.is_break && (
          <>
            {/* Catégorie + Équipes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="label block mb-1.5">Catégorie</label>
                <input
                  value={f.category}
                  onChange={(e) => setF({ ...f, category: e.target.value })}
                  placeholder="U13 A…"
                  className="input-box"
                  data-testid="game-edit-category"
                />
              </div>
              <div>
                <label className="label block mb-1.5">Équipe A</label>
                <input
                  value={f.team_a}
                  onChange={(e) => setF({ ...f, team_a: e.target.value })}
                  placeholder="Lions"
                  className="input-box"
                  data-testid="game-edit-team-a"
                />
              </div>
              <div>
                <label className="label block mb-1.5">Équipe B</label>
                <input
                  value={f.team_b}
                  onChange={(e) => setF({ ...f, team_b: e.target.value })}
                  placeholder="Tigers"
                  className="input-box"
                  data-testid="game-edit-team-b"
                />
              </div>
            </div>

            {/* Nom de partie (phase éliminatoire) */}
            <div className="mb-4">
              <label className="label block mb-1.5">Nom de partie <span className="text-[var(--muted)] normal-case">— ex. Demi-finale, Finale, Bronze (optionnel)</span></label>
              <input
                value={f.phase_name || ""}
                onChange={(e) => setF({ ...f, phase_name: e.target.value })}
                placeholder="Quart, Demi, Finale, Bronze…"
                className="input-box"
                data-testid="game-edit-phase"
                list="phase-presets"
              />
              <datalist id="phase-presets">
                <option value="Quart de finale" />
                <option value="Demi-finale" />
                <option value="Finale" />
                <option value="Bronze" />
                <option value="Ronde de classement" />
              </datalist>
            </div>

            {/* Affectations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="label block mb-1.5">Arbitre 1</label>
                <select
                  value={f.referee1_id || ""}
                  onChange={(e) => setF({ ...f, referee1_id: e.target.value || null })}
                  className="input-box"
                  data-testid="game-edit-ref1"
                >
                  <option value="">— non assigné —</option>
                  {officials.filter((o) => Array.isArray(o.roles) && o.roles.includes("referee")).map((o) => {
                    const raw = f.date ? availIndex?.[o.id]?.[f.date] : null;
                    const ans = raw && f.start_time ? getSlotAnswer(raw, slotForTime(f.start_time, effSlots)) : (typeof raw === "string" ? raw : null);
                    const avail = ans === "yes" ? " ○" : ans === "no" ? " ✗" : "";
                    const grade = o.referee_grade_id ? "" : " · sans grade arbitre";
                    return <option key={o.id} value={o.id}>{o.first_name} {o.last_name}{avail}{grade}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="label block mb-1.5">Arbitre 2</label>
                <select
                  value={f.referee2_id || ""}
                  onChange={(e) => setF({ ...f, referee2_id: e.target.value || null })}
                  className="input-box"
                  data-testid="game-edit-ref2"
                >
                  <option value="">— non assigné —</option>
                  {officials.filter((o) => Array.isArray(o.roles) && o.roles.includes("referee")).map((o) => {
                    const raw = f.date ? availIndex?.[o.id]?.[f.date] : null;
                    const ans = raw && f.start_time ? getSlotAnswer(raw, slotForTime(f.start_time, effSlots)) : (typeof raw === "string" ? raw : null);
                    const avail = ans === "yes" ? " ○" : ans === "no" ? " ✗" : "";
                    const grade = o.referee_grade_id ? "" : " · sans grade arbitre";
                    return <option key={o.id} value={o.id}>{o.first_name} {o.last_name}{avail}{grade}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="label block mb-1.5">Marqueur</label>
                <select
                  value={f.scorer_id || ""}
                  onChange={(e) => setF({ ...f, scorer_id: e.target.value || null })}
                  className="input-box"
                  data-testid="game-edit-scorer"
                >
                  <option value="">— non assigné —</option>
                  {officials.filter((o) => Array.isArray(o.roles) && o.roles.includes("scorer")).map((o) => {
                    const raw = f.date ? availIndex?.[o.id]?.[f.date] : null;
                    const ans = raw && f.start_time ? getSlotAnswer(raw, slotForTime(f.start_time, effSlots)) : (typeof raw === "string" ? raw : null);
                    const avail = ans === "yes" ? " ○" : ans === "no" ? " ✗" : "";
                    const grade = o.scorer_grade_id ? "" : " · sans grade marqueur";
                    return <option key={o.id} value={o.id}>{o.first_name} {o.last_name}{avail}{grade}</option>;
                  })}
                </select>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">
            Annuler
          </button>
          <button onClick={submit} className="btn-primary" data-testid="game-edit-save-btn">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// =============== BREAK MODAL ===============
// Extracted to /components/event/BreakModal.jsx (Feb 27, 2026)

// =============== SHARE MODAL ===============
// Extracted to /components/event/ShareModal.jsx (Feb 27, 2026)



// =============== AVAILABILITIES TAB ===============


// =============== WORKFLOW STEPPER ===============
function EventWorkflowStepper({
  event,
  requestsCount,
  confirmedCount,
  totalAssignments,
  onStepClick,
}) {
  const playable = (event.games || []).filter((g) => !g.is_break);
  const hasGames = playable.length > 0;
  const hasAnyAssignment = playable.some(
    (g) => g.referee1_id || g.referee2_id || g.scorer_id
  );
  const assignmentsPublished = !!event.assignments_published_at;
  const allConfirmed = totalAssignments > 0 && confirmedCount >= totalAssignments;
  const isClosed = !!event.closed;

  // Détermine l'étape active automatiquement
  let activeStep = 1;
  if (!event) activeStep = 1;
  else if (requestsCount === 0) activeStep = 2;
  else if (!hasGames) activeStep = 3;
  else if (!hasAnyAssignment) activeStep = 4;
  else if (!assignmentsPublished) activeStep = 5;
  else if (totalAssignments > 0 && !allConfirmed) activeStep = 6;
  else if (!isClosed) activeStep = 7;
  else activeStep = 8;

  const steps = [
    { id: "configure", label: "Configurer", subLabel: "Détails de l'événement", tooltip: "Événement créé" },
    { id: "availability", label: "Disponibilités", subLabel: "Demander par SMS", tooltip: "Envoie la demande de dispos par SMS" },
    { id: "build", label: "Bâtir l'horaire", subLabel: "Ajouter des parties", tooltip: "Crée les parties dans l'horaire" },
    { id: "assign", label: "Assigner", subLabel: "Choisir officiels", tooltip: "Assigne les officiels aux parties" },
    { id: "publish", label: "Diffuser", subLabel: "Notifier les officiels", tooltip: "Notifie les officiels que leurs parties sont prêtes" },
    {
      id: "confirm",
      label: "Confirmations",
      subLabel:
        totalAssignments > 0 ? `${confirmedCount}/${totalAssignments} reçues` : "En attente",
      tooltip: "Attends que les officiels confirment leurs parties",
    },
    { id: "close", label: "Fermer", subLabel: "Verrouiller pour la paie", tooltip: "Verrouille l'horaire pour la paie" },
    { id: "payroll", label: "Paie + Facture", subLabel: "Calculer & exporter", tooltip: "Calcule la paie et génère la facture client" },
  ];

  return (
    <EventStepper
      steps={steps}
      activeStep={activeStep}
      onStepClick={(s, stepNum) => onStepClick && onStepClick(s, stepNum, activeStep)}
      headerAction={<StepperOnboardingButton />}
    />
  );
}


// =============== STEP ACTIONS BAR ===============
function StepActionsBar({
  event,
  requestsCount,
  confirmedCount,
  totalAssignments,
  overrideStep,
  onResetOverride,
  onOpenAvailRequest,
  onToggleLock,
  onImportExcel,
  onSwitchTab,
  onPublishAssignments,
  onUnpublishAssignments,
  onRemindUnconfirmed,
  onCloseEvent,
  onReopenEvent,
  onPublishSchedule,
  onGoToPayroll,
}) {
  const playable = (event.games || []).filter((g) => !g.is_break);
  const hasGames = playable.length > 0;
  const hasAnyAssignment = playable.some((g) => g.referee1_id || g.referee2_id || g.scorer_id);
  const assignmentsPublished = !!event.assignments_published_at;
  const allConfirmed = totalAssignments > 0 && confirmedCount >= totalAssignments;
  const isClosed = !!event.closed;
  // Unique dates of playable games (sorted) — used by DatePublishMenu for multi-day events
  const eventDates = [...new Set(playable.map((g) => g.date).filter(Boolean))].sort();

  // Détermine l'étape active (auto)
  let computedActive = 1;
  if (requestsCount === 0) computedActive = 2;
  else if (!hasGames) computedActive = 3;
  else if (!hasAnyAssignment) computedActive = 4;
  else if (!assignmentsPublished) computedActive = 5;
  else if (totalAssignments > 0 && !allConfirmed) computedActive = 6;
  else if (!isClosed) computedActive = 7;
  else computedActive = 8;

  // Override par clic dans le stepper
  const activeStep = overrideStep || computedActive;
  const isOverriding = overrideStep && overrideStep !== computedActive;

  // Définit le contenu (titre + boutons) selon l'étape
  const stepUI = {
    1: {
      title: "Étape 1 — Configuration de l'événement",
      hint: "L'événement est créé. Tu peux modifier ses métadonnées (nom, date, lieu, surfaces, durée par défaut) depuis la page Événements.",
      actions: (
        <span className="inline-flex items-center">
          <button onClick={() => onSwitchTab("schedule")} className="btn-primary" data-testid="step-action-go-schedule-1">
            Continuer vers l'horaire
          </button>
          <HelpHint
            title="Continuer vers l'horaire"
            text="Ouvre l'onglet Horaire où tu pourras commencer à ajouter des parties (manuellement) ou les importer depuis Excel."
            testid="hint-step1-continue"
          />
        </span>
      ),
    },
    2: {
      title: "Étape 2 — Demande de disponibilités",
      hint: "Envoie un SMS aux officiels pour savoir lesquels sont disponibles aux dates de l'événement.",
      actions: (
        <>
          <span className="inline-flex items-center">
            <button onClick={onOpenAvailRequest} className="btn-primary" data-testid="step-action-request-avail">
              <Send size={14} strokeWidth={2.5} /> Demander dispos par SMS
            </button>
            <HelpHint
              title="Demander dispos par SMS"
              text="Ouvre un modal où tu choisis les officiels et les dates. Un SMS personnalisé avec un lien vers leur tableau de bord est envoyé via Twilio. Sans clés Twilio, le SMS est loggé en mode mock dans la base."
              testid="hint-request-avail"
            />
          </span>
          <span className="inline-flex items-center">
            <button onClick={() => onSwitchTab("availabilities")} className="btn-secondary" data-testid="step-action-view-avail">
              Voir les réponses
            </button>
            <HelpHint
              title="Voir les réponses"
              text="Bascule sur l'onglet Disponibilités qui affiche une matrice officiels × dates avec ✓ (OUI) ou ✗ (NON) et les commentaires éventuels."
              testid="hint-view-avail"
            />
          </span>
        </>
      ),
    },
    3: {
      title: "Étape 3 — Bâtir l'horaire",
      hint: "Crée les parties (manuellement ou via import Excel) sur la base des dispos reçues.",
      actions: (
        <>
          <span className="inline-flex items-center">
            <button onClick={onImportExcel} className="btn-primary" data-testid="step-action-import-excel">
              <Upload size={14} strokeWidth={2.5} /> Import Excel
            </button>
            <HelpHint
              title="Import Excel"
              text="Ouvre un wizard 4 étapes pour importer un fichier .xlsx, .xls ou .csv. Mapping auto des colonnes, fallback durée si absente, et choix entre Append ou Replace."
              testid="hint-import-excel"
            />
          </span>
          <span className="inline-flex items-center">
            <button onClick={() => onSwitchTab("schedule")} className="btn-secondary" data-testid="step-action-go-schedule">
              Aller à l'horaire
            </button>
            <HelpHint
              title="Aller à l'horaire"
              text="Bascule sur l'onglet Horaire pour ajouter une partie manuellement avec le bouton « + Partie » et configurer heure, durée, surface, catégorie, équipes."
              testid="hint-go-schedule"
            />
          </span>
          <span className="inline-flex items-center">
            <button onClick={onToggleLock} className="btn-secondary" data-testid="step-action-toggle-lock">
              {event.availability_locked ? (
                <><Lock size={14} strokeWidth={2.5} /> Dispos verrouillées</>
              ) : (
                <><Unlock size={14} strokeWidth={2.5} /> Verrouiller dispos</>
              )}
            </button>
            <HelpHint
              title="Verrouiller les disponibilités"
              text="Empêche les officiels de modifier leurs réponses OUI/NON après que tu as commencé à assigner. Recommandé avant de bâtir l'horaire pour éviter qu'un officiel change d'avis en plein milieu."
              testid="hint-toggle-lock"
            />
          </span>
        </>
      ),
    },
    4: {
      title: "Étape 4 — Assigner les officiels",
      hint: "Dans l'horaire, assigne les officiels disponibles à chaque partie. Le picker groupe par état (✓ Dispo / ? Sans réponse / ✗ Indispo).",
      actions: (
        <>
          <span className="inline-flex items-center">
            <button onClick={() => onSwitchTab("schedule")} className="btn-primary" data-testid="step-action-go-assign">
              Aller à l'horaire
            </button>
            <HelpHint
              title="Aller à l'horaire pour assigner"
              text="Le picker d'officiels est groupé en 3 sections : ✓ Disponibles (ont dit OUI), ? Sans réponse, ✗ Indisponibles. Pioche en priorité dans la première section."
              testid="hint-go-assign"
            />
          </span>
          <span className="inline-flex items-center">
            <button onClick={onImportExcel} className="btn-secondary" data-testid="step-action-import-excel-4">
              <Upload size={14} strokeWidth={2.5} /> Import Excel
            </button>
            <HelpHint
              title="Import Excel (étape 4)"
              text="Tu peux importer un fichier qui contient DÉJÀ les officiels assignés si ton fichier source a des colonnes Arbitre 1 / Arbitre 2 / Marqueur avec les noms ou les IDs."
              testid="hint-import-excel-4"
            />
          </span>
        </>
      ),
    },
    5: {
      title: "Étape 5 — Diffuser les assignations",
      hint: "Notifie les officiels par SMS que leurs parties sont prêtes. Pour un événement multi-jours, tu peux diffuser une journée à la fois.",
      actions: (
        <span className="inline-flex items-center">
          <DatePublishMenu
            dates={eventDates}
            publishedDates={event.assignments_published_dates || []}
            onPublish={(date) => onPublishAssignments(date)}
            mode="publish"
            variant="primary"
            testid="step-action-publish-assignments"
          />
          <HelpHint
            title="Diffusion des assignations"
            text="Au clic : SMS envoyé à chaque officiel assigné à la portée choisie avec le nombre de parties + lien de connexion. Pour un événement multi-jours, tu peux choisir « Tout l'événement » OU diffuser une journée à la fois (utile quand l'horaire du Jour 2 n'est pas encore finalisé)."
            testid="hint-publish-assignments"
          />
        </span>
      ),
    },
    6: {
      title: "Étape 6 — Attente des confirmations",
      hint: `${confirmedCount}/${totalAssignments} confirmées. Envoie un rappel SMS aux officiels qui n'ont pas encore confirmé leurs parties.`,
      actions: (
        <>
          <span className="inline-flex items-center">
            <button onClick={onRemindUnconfirmed} className="btn-primary" data-testid="step-action-remind">
              <Bell size={14} strokeWidth={2.5} /> Rappel SMS aux non-confirmés
            </button>
            <HelpHint
              title="Rappel SMS"
              text="Envoie un SMS UNIQUEMENT aux officiels qui ont des parties assignées mais qui n'ont pas encore cliqué « Confirmer » dans leur tableau de bord. Idéal 48h avant l'événement."
              testid="hint-remind"
            />
          </span>
          <span className="inline-flex items-center">
            <DatePublishMenu
              dates={eventDates}
              publishedDates={event.assignments_published_dates || []}
              onPublish={(date) => onPublishAssignments(date)}
              mode="publish"
              variant="secondary"
              testid="step-action-republish"
            />
            <HelpHint
              title="Re-diffuser"
              text="Notifie par SMS les officiels assignés mais pas encore notifiés (anti-spam intégré). Pour un événement multi-jours, tu peux re-diffuser une journée précise si tu as modifié les assignations de cette date."
              testid="hint-republish"
            />
          </span>
          <span className="inline-flex items-center">
            <DatePublishMenu
              dates={eventDates}
              publishedDates={event.assignments_published_dates || []}
              onUnpublish={(date) => onUnpublishAssignments(date)}
              mode="unpublish"
              variant="ghost"
              testid="step-action-unpublish"
            />
            <HelpHint
              title="Masquer les assignations"
              text="Cache à nouveau les parties aux officiels (totalement ou pour une journée). Utile si tu réalises une grosse erreur dans l'horaire d'un jour donné. Aucun SMS n'est envoyé."
              testid="hint-unpublish"
            />
          </span>
        </>
      ),
    },
    7: {
      title: "Étape 7 — Fermer l'événement",
      hint: "Verrouille l'horaire pour générer les paies. Tu pourras toujours rouvrir si nécessaire.",
      actions: (
        <>
          <span className="inline-flex items-center">
            <button onClick={onCloseEvent} className="btn-primary" data-testid="step-action-close">
              <CheckSquare size={14} strokeWidth={2.5} /> Fermer l'événement
            </button>
            <HelpHint
              title="Fermer l'événement"
              text="L'horaire devient en lecture seule. La paie devient calculable dans /paies. Tu peux rouvrir à tout moment pour corriger une assignation (mais la paie sera recalculée)."
              testid="hint-close"
            />
          </span>
          {!event.published_at ? (
            <span className="inline-flex items-center">
              <button onClick={onPublishSchedule} className="btn-secondary" data-testid="step-action-publish-schedule">
                Publier l'horaire client
              </button>
              <HelpHint
                title="Publier l'horaire client"
                text="Crée un snapshot qui sera servi à l'URL publique /horaire/:id. C'est ce que tu donnes au tournoi/client. Différent de « Diffuser les assignations » qui s'adresse aux officiels."
                testid="hint-publish-schedule"
              />
            </span>
          ) : (
            <span className="inline-flex items-center">
              <button onClick={onPublishSchedule} className="btn-secondary" data-testid="step-action-republish-schedule">
                Republier (changements)
              </button>
              <HelpHint
                title="Republier l'horaire client"
                text="L'horaire client a déjà été publié. Si tu as fait des changements depuis, ce bouton met à jour le snapshot servi à l'URL publique."
                testid="hint-republish-schedule"
              />
            </span>
          )}
        </>
      ),
    },
    8: {
      title: "Étape 8 — Paie + Facture",
      hint: "Calcule la paie de chaque officiel et exporte la facture client en PDF.",
      actions: (
        <>
          <span className="inline-flex items-center">
            <button onClick={onGoToPayroll} className="btn-primary" data-testid="step-action-payroll">
              Aller aux paies
            </button>
            <HelpHint
              title="Aller aux paies"
              text="Te dirige vers /paies. Pour chaque officiel tu vois : Base (parties × tarif du grade) + Extras (essence, bonus) - Déductions (vêtements, etc.). Tu peux ajouter/modifier ces lignes inline."
              testid="hint-payroll"
            />
          </span>
          <span className="inline-flex items-center">
            <button onClick={onReopenEvent} className="btn-secondary" data-testid="step-action-reopen">
              Rouvrir l'événement
            </button>
            <HelpHint
              title="Rouvrir l'événement"
              text="Annule la fermeture pour pouvoir modifier l'horaire/les assignations. La paie sera recalculée automatiquement à la prochaine fermeture."
              testid="hint-reopen"
            />
          </span>
        </>
      ),
    },
  };

  const ui = stepUI[activeStep];
  if (!ui) return null;

  return (
    <div
      className="rounded-2xl p-5 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4"
      style={{
        background: "var(--surface)",
        border: isOverriding ? "2px dashed var(--text)" : "1px solid var(--border-strong)",
      }}
      data-testid="step-actions-bar"
    >
      <div className="flex-1 min-w-0">
        <div className="label mb-1 flex items-center gap-2">
          {ui.title}
          {isOverriding && (
            <button
              onClick={onResetOverride}
              className="text-[10px] uppercase tracking-widest font-semibold text-[var(--muted)] hover:text-[var(--text)] transition-colors border border-[var(--border-strong)] rounded-md px-2 py-0.5"
              data-testid="step-action-reset-override"
              title="Revenir à l'étape actuelle"
            >
              ← Étape actuelle ({computedActive})
            </button>
          )}
        </div>
        <p className="text-sm text-[var(--muted)] leading-relaxed">{ui.hint}</p>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">{ui.actions}</div>
    </div>
  );
}
